/**
 * Meeting Detail Page
 * ===================
 * Dedicated page for viewing individual meeting details
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody, Button, Badge, ConfirmationModal, Modal } from '../../components/ui';
import { DashboardLayout } from '../../components/layout';
import { leadAPI } from '../../api';
import transcriptAPI from '../../api/transcripts';
import meetingAPI from '../../api/meetings';
import mockAPI from '../../api/mock';
import { useToast, usePermissions } from '../../hooks';
import { isDevOrStaging } from '../../utils/environment';
import { parseTranscriptText } from '../../utils/transcripts';
import { isHttpUrl } from '../../utils/validation';
import TranscriptTabView from '../../components/TranscriptTabView';
import LinkLeadModal from '../../components/meetings/LinkLeadModal';

const MeetingDetailPage = () => {
  const { leadId, meetingId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { is } = usePermissions();
  const canModifyMeeting = !is.viewer() && !is.complianceOfficer();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [transcriptStatus, setTranscriptStatus] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [fetchingTranscript, setFetchingTranscript] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [transcriptDataLoading, setTranscriptDataLoading] = useState(false);

  // Meeting notes modal state
  const [showMeetingNotesModal, setShowMeetingNotesModal] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [savingMeetingNotes, setSavingMeetingNotes] = useState(false);

  // Manual transcript modal state
  const [showManualTranscriptModal, setShowManualTranscriptModal] = useState(false);
  const [manualTranscriptText, setManualTranscriptText] = useState('');
  const [savingManualTranscript, setSavingManualTranscript] = useState(false);

  // Mock transcript state
  const [generatingMockTranscript, setGeneratingMockTranscript] = useState(false);

  // Knowledge extraction state
  const [extractingKnowledge, setExtractingKnowledge] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);

  // Discovery call classification state
  const [classification, setClassification] = useState(null);
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [settingClassification, setSettingClassification] = useState(false);

  // Create lead modal state
  const [showCreateLeadModal, setShowCreateLeadModal] = useState(false);
  const [createLeadForm, setCreateLeadForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    designation: '',
    notes: '',
  });
  const [creatingLead, setCreatingLead] = useState(false);

  // Knowledge/structure processing state
  const [processingKnowledge, setProcessingKnowledge] = useState(false);
  const [knowledgeResult, setKnowledgeResult] = useState(null);

  // Link-to-lead modal state (orphan meetings only)
  const [showLinkLeadModal, setShowLinkLeadModal] = useState(false);

  // An orphan (unlinked) meeting can be linked to an existing lead from here,
  // regardless of its classification.
  const isOrphanMeeting = !leadId && !meeting?.lead_id;

  useEffect(() => {
    const fetchMeetingDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await leadAPI.getMeeting(meetingId);
        if (response.data) {
          setMeeting(response.data);
        }
      } catch (err) {
        console.error('Error fetching meeting details:', err);
        setError(err.message || 'Failed to load meeting details');
        showToast('Failed to load meeting details', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (meetingId) {
      fetchMeetingDetails();
      fetchTranscriptStatus();
    }
  }, [meetingId, showToast]);

  useEffect(() => {
    const fetchClassification = async () => {
      if (!meetingId || leadId) return;
      setClassificationLoading(true);
      try {
        const response = await meetingAPI.getClassification(meetingId);
        if (response.data) {
          setClassification(response.data);
        }
      } catch (err) {
        console.log('Classification not available');
      } finally {
        setClassificationLoading(false);
      }
    };

    if (meetingId && !leadId) {
      fetchClassification();
    }
  }, [meetingId, leadId]);

  const fetchTranscriptStatus = async () => {
    setTranscriptLoading(true);
    try {
      const response = await transcriptAPI.getTranscriptStatus(meetingId);
      if (response.data) {
        setTranscriptStatus(response.data);
        // If transcript is available, fetch the full transcript data
        if (response.data.has_transcript) {
          fetchTranscriptData();
        }
      }
    } catch (err) {
      // Transcript may not exist yet, which is fine
      console.log('Transcript status not available');
    } finally {
      setTranscriptLoading(false);
    }
  };

  const fetchTranscriptData = async () => {
    if (!meetingId) return;

    setTranscriptDataLoading(true);
    try {
      // Always use standalone endpoint — works for both linked and orphan meetings
      // and returns root-level full_text (handles manual, tldv, recall transcripts,
      // plus retired legacy sources)
      let response;
      response = await meetingAPI.getMeetingTranscript(meetingId);
      if (response.data) {
        setTranscript(response.data);
        setMeetingNotes(response.data.meeting_notes || '');
      }
    } catch (err) {
      console.error('Error fetching transcript data:', err);
    } finally {
      setTranscriptDataLoading(false);
    }
  };

  const handleBack = () => {
    if (leadId) {
      navigate(`/leads/${leadId}`);
    } else if (meeting?.lead_id) {
      navigate(`/leads/${meeting.lead_id}`);
    } else {
      navigate('/meetings');
    }
  };

  const handleStatusUpdateClick = (newStatus) => {
    setPendingStatus(newStatus);
    setShowStatusModal(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!pendingStatus) return;

    setStatusUpdateLoading(true);
    try {
      const response = await leadAPI.updateMeetingStatus(meetingId, { status: pendingStatus });
      // Update meeting with new status - UI will lock if status is completed/cancelled/no_show
      setMeeting((prev) => ({
        ...prev,
        status: pendingStatus,
        status_changed: true  // Backend sets this to true after first change
      }));
      setShowStatusModal(false);
      setPendingStatus(null);
      showToast('Meeting status updated successfully', 'success');
    } catch (err) {
      console.error('Error updating meeting status:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to update meeting status';
      showToast(errorMessage, 'error');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleCancelStatusUpdate = () => {
    setShowStatusModal(false);
    setPendingStatus(null);
  };

  const handleFetchTranscript = async () => {
    setFetchingTranscript(true);
    try {
      const response = await leadAPI.fetchTranscriptManually(leadId, meetingId);
      if (response.data) {
        showToast('Transcript fetch initiated. Processing in background...', 'success');
        // Refresh transcript status after a delay
        setTimeout(() => {
          fetchTranscriptStatus();
        }, 3000);
      }
    } catch (err) {
      console.error('Error fetching transcript:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to fetch transcript';
      showToast(errorMessage, 'error');
    } finally {
      setFetchingTranscript(false);
    }
  };

  const handleOpenMeetingNotesModal = () => {
    setShowMeetingNotesModal(true);
  };

  const handleCloseMeetingNotesModal = () => {
    setShowMeetingNotesModal(false);
    // Reset to original value if user cancels
    setMeetingNotes(transcript?.meeting_notes || '');
  };

  const handleSaveMeetingNotes = async () => {
    setSavingMeetingNotes(true);
    try {
      let response;
      if (leadId) {
        response = await transcriptAPI.updateMeetingNotes(leadId, meetingId, meetingNotes);
      } else {
        // Standalone endpoint for discovery calls / orphan meetings (no lead_id)
        response = await meetingAPI.updateMeetingNotes(meetingId, meetingNotes);
      }
      if (response.data) {
        // Update the transcript state with the new notes
        setTranscript(prev => ({
          ...prev,
          meeting_notes: response.data.notes
        }));
        showToast('Meeting notes saved successfully', 'success');
        setShowMeetingNotesModal(false);
      }
    } catch (err) {
      console.error('Error saving meeting notes:', err);
      showToast('Failed to save meeting notes', 'error');
    } finally {
      setSavingMeetingNotes(false);
    }
  };

  const handleSaveManualTranscript = async () => {
    if (!manualTranscriptText.trim()) {
      showToast('Please enter transcript text', 'error');
      return;
    }

    setSavingManualTranscript(true);
    try {
      let response;
      if (leadId) {
        response = await leadAPI.saveManualTranscript(leadId, meetingId, {
          transcript_text: manualTranscriptText
        });
      } else {
        // Standalone endpoint for discovery calls / orphan meetings
        response = await meetingAPI.saveManualTranscript(meetingId, {
          transcript_text: manualTranscriptText
        });
      }

      if (response.data) {
        showToast('Manual transcript saved successfully', 'success');
        setShowManualTranscriptModal(false);
        setManualTranscriptText('');

        // Update meeting status to completed (backend automatically sets this)
        setMeeting((prev) => ({
          ...prev,
          status: 'completed',
          transcript_status: 'completed'
        }));

        // Refresh transcript status and data
        await fetchTranscriptStatus();
        if (response.data.meeting_id) {
          await fetchTranscriptData();
        }
      }
    } catch (err) {
      console.error('Error saving manual transcript:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save manual transcript';
      showToast(errorMessage, 'error');
    } finally {
      setSavingManualTranscript(false);
    }
  };

  const handleCloseManualTranscriptModal = () => {
    setShowManualTranscriptModal(false);
    setManualTranscriptText('');
  };


  const handleGenerateMockTranscript = async () => {
    setGeneratingMockTranscript(true);
    try {
      const response = await mockAPI.generateMockTranscript(leadId, meetingId);
      if (response.data) {
        showToast('Mock transcript generated successfully. Questionnaire processing queued.', 'success');
        setMeeting((prev) => ({
          ...prev,
          status: 'completed',
          transcript_status: 'completed',
        }));
        await fetchTranscriptStatus();
        await fetchTranscriptData();
      }
    } catch (err) {
      console.error('Error generating mock transcript:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to generate mock transcript';
      showToast(errorMessage, 'error');
    } finally {
      setGeneratingMockTranscript(false);
    }
  };

  const handleExtractKnowledge = async () => {
    setExtractingKnowledge(true);
    setExtractionResult(null);
    try {
      const response = await meetingAPI.extractMeetingKnowledge(meetingId);
      if (response.data) {
        setExtractionResult(response.data);
        const count = response.data.drafts_created || 0;
        if (count > 0) {
          showToast(`${count} knowledge update draft(s) created for admin review`, 'success');
        } else {
          showToast('No knowledge updates identified from this transcript', 'info');
        }
      }
    } catch (err) {
      console.error('Error extracting knowledge:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to extract knowledge from transcript';
      showToast(errorMessage, 'error');
    } finally {
      setExtractingKnowledge(false);
    }
  };

  const handleClassifyCall = async () => {
    setClassifying(true);
    try {
      const response = await meetingAPI.classifyDiscoveryCall(meetingId);
      if (response.data) {
        setClassification({
          classification: response.data.classification,
          classification_key_points: {
            key_points: response.data.key_points || [],
            rationale: response.data.rationale || '',
            extracted_lead_info: response.data.extracted_lead_info || null,
          },
          classified_at: new Date().toISOString(),
          is_discovery_call: true,
        });
        showToast(`Call classified as: ${response.data.classification?.replace(/_/g, ' ')}`, 'success');
      }
    } catch (err) {
      console.error('Error classifying call:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to classify call';
      showToast(errorMessage, 'error');
    } finally {
      setClassifying(false);
    }
  };

  // Manual override of the classification (distinct from AI re-classify). Sets the
  // value directly and updates local state from the response so the matching action
  // button swaps in without a refetch.
  const handleSetClassification = async (value) => {
    if (!value || value === classification?.classification) return;
    setSettingClassification(true);
    try {
      const response = await meetingAPI.setMeetingClassification(meetingId, value);
      if (response.data) {
        setClassification({
          classification: response.data.classification,
          classification_key_points: response.data.classification_key_points,
          classified_at: response.data.classified_at,
          is_discovery_call: response.data.is_discovery_call,
        });
        showToast(`Classification changed to: ${response.data.classification?.replace(/_/g, ' ')}`, 'success');
      }
    } catch (err) {
      console.error('Error changing classification:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to change classification';
      showToast(errorMessage, 'error');
    } finally {
      setSettingClassification(false);
    }
  };

  const handleCreateLead = async () => {
    if (!createLeadForm.name || !createLeadForm.company) {
      showToast('Name and company are required', 'error');
      return;
    }

    setCreatingLead(true);
    try {
      const response = await meetingAPI.createLeadFromDiscoveryCall(meetingId, createLeadForm);
      if (response.data) {
        showToast(`Lead '${response.data.lead_name}' created successfully`, 'success');
        setShowCreateLeadModal(false);
        // Navigate to the new lead
        navigate(`/leads/${response.data.lead_id}`);
      }
    } catch (err) {
      console.error('Error creating lead:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to create lead';
      showToast(errorMessage, 'error');
    } finally {
      setCreatingLead(false);
    }
  };

  const handleProcessKnowledge = async () => {
    setProcessingKnowledge(true);
    setKnowledgeResult(null);
    try {
      const response = await meetingAPI.processForKnowledgeUpdate(meetingId);
      if (response.data) {
        setKnowledgeResult(response.data);
        const count = response.data.drafts_created || 0;
        if (count > 0) {
          showToast(`${count} knowledge update draft(s) created`, 'success');
        } else {
          showToast('No knowledge updates identified', 'info');
        }
      }
    } catch (err) {
      console.error('Error processing knowledge:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to process for knowledge update';
      showToast(errorMessage, 'error');
    } finally {
      setProcessingKnowledge(false);
    }
  };

  const handleCreateUpdateRequest = async () => {
    setProcessingKnowledge(true);
    setKnowledgeResult(null);
    try {
      const response = await meetingAPI.createUpdateRequest(meetingId);
      if (response.data) {
        setKnowledgeResult(response.data);
        const count = response.data.drafts_created || 0;
        if (count > 0) {
          showToast(`${count} structure update draft(s) created`, 'success');
        } else {
          showToast('No structure updates identified', 'info');
        }
      }
    } catch (err) {
      console.error('Error creating update request:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to create update request';
      showToast(errorMessage, 'error');
    } finally {
      setProcessingKnowledge(false);
    }
  };

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { variant: 'info', label: 'Scheduled' },
      completed: { variant: 'success', label: 'Completed' },
      cancelled: { variant: 'danger', label: 'Cancelled' },
      no_show: { variant: 'warning', label: 'No Show' },
    };
    const config = statusConfig[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatPlatform = (platform) => {
    return platform.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTranscriptStatusBadge = (status) => {
    const statusConfig = {
      none: { variant: 'default', label: 'No Transcript' },
      pending: { variant: 'warning', label: 'Pending' },
      processing: { variant: 'info', label: 'Processing' },
      completed: { variant: 'success', label: 'Available' },
      failed: { variant: 'danger', label: 'Failed' },
    };
    const config = statusConfig[status] || { variant: 'default', label: status || 'Unknown' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="px-4">
          <Card>
            <CardBody>
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading meeting details...</p>
              </div>
            </CardBody>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !meeting) {
    return (
      <DashboardLayout>
        <div className="px-4">
          <Card>
            <CardBody>
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">{error || 'Meeting not found'}</p>
                <Button onClick={handleBack}>← Back</Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="px-4">
        {/* Page Header */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-sans font-bold text-gray-900 dark:text-white mb-1">
              Meeting Details
            </h1>
            <p className="text-sm text-gray-600">
              View and manage meeting information
            </p>
          </div>
          <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0">
            {transcriptStatus?.has_transcript && transcript && (
              <>
                {/* Show "Update Brain" only for linked meetings */}
                {(leadId || meeting?.lead_id) && (
                  <button
                    onClick={handleExtractKnowledge}
                    disabled={extractingKnowledge}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-600 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    title="Analyze transcript and suggest knowledge base updates"
                  >
                    {extractingKnowledge ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span>Update Brain</span>
                      </>
                    )}
                  </button>
                )}
              </>
            )}
            {/* Header Link to Lead covers the unclassified / no-card case; once
                classified, the classification card hosts its own Link to Lead. */}
            {isOrphanMeeting && canModifyMeeting && !classification?.classification && (
              <Button variant="secondary" size="sm" onClick={() => setShowLinkLeadModal(true)}>
                Link to Lead
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleBack}>
              {leadId || meeting?.lead_id ? '← Back to Lead' : '← Back to Meetings'}
            </Button>
          </div>
        </div>

        {/* Knowledge Extraction Result */}
        {extractionResult && (
          <div className={`mb-4 rounded-lg border p-4 ${extractionResult.drafts_created > 0 ? 'bg-primary/5 border-primary/20' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${extractionResult.drafts_created > 0 ? 'bg-primary/10' : 'bg-gray-100'}`}>
                  <svg className={`w-5 h-5 ${extractionResult.drafts_created > 0 ? 'text-primary' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <p className={`text-sm font-medium ${extractionResult.drafts_created > 0 ? 'text-primary-900' : 'text-gray-700'}`}>
                    {extractionResult.message}
                  </p>
                  {extractionResult.drafts_created > 0 && extractionResult.drafts && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {extractionResult.drafts.map((d) => (
                        <span key={d.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                          {d.draft_type === 'create' ? '+' : '~'} {d.category}/{d.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {extractionResult.drafts_created > 0 && (
                  <button
                    onClick={() => navigate('/brain/drafts')}
                    className="text-sm font-medium text-primary hover:text-primary-600 underline"
                  >
                    Review Drafts
                  </button>
                )}
                <button
                  onClick={() => setExtractionResult(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Discovery Call Classification Card */}
        {!leadId && !meeting?.lead_id && (
          <div className="mb-4">
            {classificationLoading ? (
              <Card>
                <CardBody className="p-4">
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Loading classification...</p>
                  </div>
                </CardBody>
              </Card>
            ) : classification?.classification ? (
              <Card className={`border-l-4 ${
                classification.classification === 'new_lead' ? 'border-l-green-500 bg-green-50/50' :
                classification.classification === 'knowledge_update' ? 'border-l-blue-500 bg-blue-50/50' :
                classification.classification === 'structure_data_update' ? 'border-l-amber-500 bg-amber-50/50' :
                'border-l-gray-500'
              }`}>
                <CardBody className="p-4">
                  {/* Title row: heading + badge on the left; primary action +
                      Link to Lead on the right */}
                  <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-semibold text-gray-900">Discovery Call Classification</h3>
                      <Badge variant={
                        classification.classification === 'new_lead' ? 'success' :
                        classification.classification === 'knowledge_update' ? 'info' :
                        classification.classification === 'structure_data_update' ? 'warning' : 'default'
                      }>
                        {classification.classification === 'new_lead' ? 'New Lead' :
                         classification.classification === 'knowledge_update' ? 'Knowledge Update' :
                         classification.classification === 'structure_data_update' ? 'Structure Data Update' :
                         classification.classification}
                      </Badge>
                    </div>
                    {canModifyMeeting && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {classification.classification === 'new_lead' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              // Pre-fill form from AI-extracted lead info
                              const info = classification?.classification_key_points?.extracted_lead_info;
                              // Find external attendee email (not from internal domains)
                              const internalDomains = ['zigchain.com', 'tractionboard.io', 'zamanathq.com', 'zignaly.com'];
                              let attendeeEmail = '';
                              if (meeting?.attendees?.length) {
                                const externalAttendee = meeting.attendees.find(a => {
                                  if (!a || !a.includes('@')) return false;
                                  const domain = a.split('@')[1]?.toLowerCase();
                                  return domain && !internalDomains.includes(domain);
                                });
                                if (externalAttendee) attendeeEmail = externalAttendee;
                              }
                              setCreateLeadForm({
                                name: info?.name || '',
                                company: info?.company || '',
                                email: info?.email || attendeeEmail,
                                phone: info?.phone || '',
                                designation: info?.designation || '',
                                notes: info?.notes || '',
                              });
                              setShowCreateLeadModal(true);
                            }}
                          >
                            Create Lead
                          </Button>
                        )}
                        {classification.classification === 'knowledge_update' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleProcessKnowledge}
                            disabled={processingKnowledge}
                          >
                            {processingKnowledge ? 'Processing...' : 'Process Knowledge'}
                          </Button>
                        )}
                        {classification.classification === 'structure_data_update' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleCreateUpdateRequest}
                            disabled={processingKnowledge}
                          >
                            {processingKnowledge ? 'Processing...' : 'Create Update Request'}
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowLinkLeadModal(true)}
                        >
                          Link to Lead
                        </Button>
                      </div>
                    )}
                  </div>
                  {classification.classification_key_points?.rationale && (
                    <p className="text-sm text-gray-600 mb-3">{classification.classification_key_points.rationale}</p>
                  )}
                  {classification.classification_key_points?.key_points?.length > 0 && (
                    <ul className="space-y-1.5">
                      {classification.classification_key_points.key_points.map((kp, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5"></span>
                          <div>
                            <span className="text-gray-800">{kp.point}</span>
                            {kp.relevance && (
                              <span className="text-gray-500 ml-1">— {kp.relevance}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Footer: secondary controls — change classification + re-classify */}
                  {canModifyMeeting && (
                    <div className="mt-4 pt-3 border-t border-gray-200/70 dark:border-[#3A3C3E] flex flex-wrap items-end justify-end gap-2">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-500">Change classification</label>
                        <select
                          value={classification.classification}
                          onChange={(e) => handleSetClassification(e.target.value)}
                          disabled={settingClassification}
                          title="Set the classification yourself"
                          className="px-3 py-1.5 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary disabled:opacity-50"
                        >
                          <option value="new_lead">New Lead</option>
                          <option value="knowledge_update">Knowledge Update</option>
                          <option value="structure_data_update">Structure Data Update</option>
                        </select>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClassifyCall}
                        disabled={classifying}
                        title="Re-run the AI classifier on this transcript"
                      >
                        {classifying ? 'Re-classifying...' : 'Re-classify'}
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            ) : transcriptStatus?.has_transcript && transcript ? (
              <Card className="border-l-4 border-l-gray-300">
                <CardBody className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">Discovery Call — Not Yet Classified</h3>
                      <p className="text-sm text-gray-500">This meeting is not linked to any lead. Classify it to determine the next action.</p>
                    </div>
                    {canModifyMeeting && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleClassifyCall}
                        disabled={classifying}
                        className="w-full sm:w-auto justify-center sm:flex-shrink-0"
                      >
                        {classifying ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
                            Classifying...
                          </>
                        ) : 'Classify Call'}
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            ) : null}

            {/* Knowledge/Structure Processing Result */}
            {knowledgeResult && (
              <div className={`mt-3 rounded-lg border p-4 ${knowledgeResult.drafts_created > 0 ? 'bg-primary/5 border-primary/20' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${knowledgeResult.drafts_created > 0 ? 'text-primary-900' : 'text-gray-700'}`}>
                      {knowledgeResult.message}
                    </p>
                    {knowledgeResult.drafts_created > 0 && knowledgeResult.drafts && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {knowledgeResult.drafts.map((d) => (
                          <span key={d.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                            {d.draft_type === 'create' ? '+' : '~'} {d.category}/{d.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {knowledgeResult.drafts_created > 0 && (
                      <button
                        onClick={() => navigate('/brain/drafts')}
                        className="text-sm font-medium text-primary hover:text-primary-600 underline"
                      >
                        Review Drafts
                      </button>
                    )}
                    <button onClick={() => setKnowledgeResult(null)} className="text-gray-400 hover:text-gray-600">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Meeting Header Card */}
        <Card className="mb-4">
          <CardBody className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-2">
                  <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white break-words">
                    {meeting.title}
                  </h2>
                  {getStatusBadge(meeting.status)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Type</p>
                    <p className="text-gray-900 font-medium capitalize">{meeting.type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Platform</p>
                    <p className="text-gray-900 font-medium">{formatPlatform(meeting.platform)}</p>
                  </div>
                </div>
              </div>
              {canModifyMeeting && isHttpUrl(meeting.meeting_link) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => window.open(meeting.meeting_link, '_blank')}
                  className="w-full sm:w-auto justify-center sm:flex-shrink-0"
                >
                  Join Meeting
                </Button>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Meeting Details Card */}
        <Card className="mb-4">
          <CardHeader className="p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg font-sans font-semibold text-gray-900 dark:text-white">
                Meeting Information
              </h3>
              {/* Status Display - Show locked status for final states */}
              {['completed', 'cancelled', 'no_show'].includes(meeting.status) ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Status locked:</span>
                  {getStatusBadge(meeting.status)}
                </div>
              ) : !canModifyMeeting ? (
                <div className="flex items-center gap-2">
                  {getStatusBadge(meeting.status)}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Update Status:</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={meeting.status === 'completed' ? 'success' : 'outline'}
                      onClick={() => handleStatusUpdateClick('completed')}
                      disabled={meeting.status === 'completed'}
                    >
                      Completed
                    </Button>
                    <Button
                      size="sm"
                      variant={meeting.status === 'cancelled' ? 'danger' : 'outline'}
                      onClick={() => handleStatusUpdateClick('cancelled')}
                      disabled={meeting.status === 'cancelled'}
                    >
                      Cancelled
                    </Button>
                    <Button
                      size="sm"
                      variant={meeting.status === 'no_show' ? 'warning' : 'outline'}
                      onClick={() => handleStatusUpdateClick('no_show')}
                      disabled={meeting.status === 'no_show'}
                    >
                      No Show
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardBody className="p-4 pt-0">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Scheduled Date & Time</p>
                <p className="text-gray-900">
                  {new Date(meeting.scheduled_at).toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700">Duration</p>
                <p className="text-gray-900">{meeting.duration_minutes} minutes</p>
              </div>

              {meeting.meeting_link && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Meeting Link</p>
                  {isHttpUrl(meeting.meeting_link) ? (
                    <a
                      href={meeting.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline break-all"
                    >
                      {meeting.meeting_link}
                    </a>
                  ) : (
                    /* A non-URL value (e.g. a bare conference id from a Recall orphan)
                       stays visible and selectable — it is the meeting's only identifier. */
                    <p className="text-gray-900 font-mono text-sm break-all">{meeting.meeting_link}</p>
                  )}
                </div>
              )}

              {meeting.platform_meeting_id && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Platform Meeting ID</p>
                  <p className="text-gray-900 font-mono text-sm">{meeting.platform_meeting_id}</p>
                </div>
              )}

              {meeting.attendees && meeting.attendees.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Attendees</p>
                  <div className="flex flex-wrap gap-2">
                    {meeting.attendees.map((attendee, idx) => (
                      <Badge key={idx} variant="info">
                        {attendee}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {meeting.agenda && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Agenda</p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900 whitespace-pre-wrap">{meeting.agenda}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-2">Metadata</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Created</p>
                    <p className="text-gray-900">
                      {new Date(meeting.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Updated</p>
                    <p className="text-gray-900">
                      {new Date(meeting.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Merged Transcript Card */}
        <Card className="mb-4">
          <CardHeader className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-sans font-semibold text-gray-900 dark:text-white">
                Meeting Transcript
              </h3>
              {transcriptStatus && getTranscriptStatusBadge(transcriptStatus.transcript_status)}
            </div>
          </CardHeader>
          <CardBody className="p-4 pt-0">
            {transcriptLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Checking transcript status...</p>
              </div>
            ) : transcriptStatus?.has_transcript && transcript ? (
              <>
                {/* Transcript Status Icons - Only show summary and action items if available */}
                {(transcriptStatus.has_summary || transcriptStatus.has_action_items) && (
                  <div className="flex items-center gap-4 text-sm mb-4 pb-4 border-b">
                    {transcriptStatus.has_summary && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Summary generated</span>
                      </div>
                    )}
                    {transcriptStatus.has_action_items && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Action items extracted</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Tabbed Transcript View */}
                <TranscriptTabView transcriptData={transcript} />
              </>
            ) : (
              <div className="text-center py-4">
                <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">
                  {transcriptStatus?.transcript_status === 'pending' || transcriptStatus?.transcript_status === 'processing'
                    ? 'Transcript is being processed. Please check back later.'
                    : 'No transcript available for this meeting yet.'}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Transcripts are automatically captured when meetings are recorded with tl;dv.
                </p>
                {canModifyMeeting && (
                <div className="mt-4 flex justify-center gap-2 flex-col items-center">
                  {meeting.tldv_meeting_id && (
                    <Button
                      onClick={handleFetchTranscript}
                      disabled={fetchingTranscript}
                      size="sm"
                    >
                      {fetchingTranscript ? 'Fetching Transcript...' : 'Add Transcript'}
                    </Button>
                  )}
                  {(!transcriptStatus?.has_transcript &&
                    (transcriptStatus?.transcript_status === 'none' ||
                     transcriptStatus?.transcript_status === 'failed') &&
                    !meeting.tldv_meeting_id) && (
                    <>
                      {isDevOrStaging() && (
                        <Button
                          onClick={handleGenerateMockTranscript}
                          disabled={generatingMockTranscript}
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
                        >
                          {generatingMockTranscript ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
                              Generating...
                            </span>
                          ) : (
                            'Mock Transcript'
                          )}
                        </Button>
                      )}
                      <Button
                        onClick={() => setShowManualTranscriptModal(true)}
                        variant="outline"
                        size="sm"
                      >
                        Add Transcript Manually
                      </Button>
                    </>
                  )}
                  {meeting.tldv_meeting_id &&
                   !transcriptStatus?.has_transcript && (
                    <div className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        title="Manual transcript not available when automatic transcripts exist"
                      >
                        Add Transcript Manually
                      </Button>
                      <p className="text-xs text-gray-400 mt-1">
                        Automatic transcript from tl;dv is expected
                      </p>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Meeting Notes Section - Only show if transcript is available */}
        {transcriptStatus?.has_transcript && transcript && (
          <Card className="mb-4">
            <CardHeader className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-sans font-semibold text-gray-900 dark:text-white">
                  Meeting Notes
                </h3>
                {canModifyMeeting && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenMeetingNotesModal}
                  >
                    {transcript.meeting_notes ? 'Edit Notes' : 'Add Notes'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody className="p-4 pt-0">
              {transcript.meeting_notes ? (
                <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {transcript.meeting_notes}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  {canModifyMeeting
                    ? 'No meeting notes added yet. Click "Add Notes" to add your notes about this meeting.'
                    : 'No meeting notes added yet.'}
                </p>
              )}
            </CardBody>
          </Card>
        )}

        {/* Loading state for transcript data */}
        {transcriptStatus?.has_transcript && transcriptDataLoading && (
          <Card>
            <CardBody className="p-4">
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading transcript content...</p>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Status Update Confirmation Modal */}
        <ConfirmationModal
          isOpen={showStatusModal}
          onClose={handleCancelStatusUpdate}
          onConfirm={handleConfirmStatusUpdate}
          title="Confirm Status Update"
          message={
            meeting && pendingStatus
              ? `Are you sure you want to update the meeting status from "${meeting.status}" to "${pendingStatus}"?`
              : 'Are you sure you want to update the status?'
          }
          confirmText="Update Status"
          cancelText="Cancel"
          confirmVariant="primary"
          isLoading={statusUpdateLoading}
        />

        {/* Meeting Notes Modal */}
        <Modal
          isOpen={showMeetingNotesModal}
          onClose={handleCloseMeetingNotesModal}
          title="Meeting Notes"
          size="lg"
          closeOnOverlayClick={!savingMeetingNotes}
          showCloseButton={!savingMeetingNotes}
          footer={
            <>
              <Button
                variant="outline"
                onClick={handleCloseMeetingNotesModal}
                disabled={savingMeetingNotes}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMeetingNotes}
                disabled={savingMeetingNotes}
              >
                {savingMeetingNotes ? 'Saving...' : 'Save Notes'}
              </Button>
            </>
          }
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add your notes about this meeting
            </label>
            <textarea
              value={meetingNotes}
              onChange={(e) => setMeetingNotes(e.target.value)}
              placeholder="Enter your meeting notes here..."
              className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary resize-none"
              disabled={savingMeetingNotes}
            />
            <p className="mt-2 text-sm text-gray-500">
              These notes will be saved to the database and synced with the meeting transcript.
            </p>
          </div>
        </Modal>

        {/* Manual Transcript Modal */}
        <Modal
          isOpen={showManualTranscriptModal}
          onClose={handleCloseManualTranscriptModal}
          title="Add Transcript Manually"
          size="lg"
          closeOnOverlayClick={!savingManualTranscript}
          showCloseButton={!savingManualTranscript}
          footer={
            <>
              <Button
                variant="outline"
                onClick={handleCloseManualTranscriptModal}
                disabled={savingManualTranscript}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveManualTranscript}
                disabled={savingManualTranscript}
              >
                {savingManualTranscript ? 'Saving...' : 'Save Transcript'}
              </Button>
            </>
          }
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste or type the meeting transcript
            </label>
            <textarea
              value={manualTranscriptText}
              onChange={(e) => setManualTranscriptText(e.target.value)}
              placeholder="Paste the full transcript text here...&#10;&#10;Example:&#10;John: Hello everyone, welcome to the meeting.&#10;Sarah: Thanks for having us.&#10;John: Let's discuss the project timeline..."
              className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary resize-none font-mono text-sm"
              disabled={savingManualTranscript}
            />
            <p className="mt-2 text-sm text-gray-500">
              The transcript will be saved and displayed in the same format as automatically captured transcripts.
            </p>
          </div>
        </Modal>

        {/* Create Lead from Discovery Call Modal */}
        <Modal
          isOpen={showCreateLeadModal}
          onClose={() => { setShowCreateLeadModal(false); setCreateLeadForm({ name: '', company: '', email: '', phone: '', designation: '', notes: '' }); }}
          title="Create Lead from Discovery Call"
          size="lg"
          closeOnOverlayClick={!creatingLead}
          showCloseButton={!creatingLead}
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => { setShowCreateLeadModal(false); setCreateLeadForm({ name: '', company: '', email: '', phone: '', designation: '', notes: '' }); }}
                disabled={creatingLead}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateLead}
                disabled={creatingLead || !createLeadForm.name || !createLeadForm.company}
              >
                {creatingLead ? 'Creating...' : 'Create Lead'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={createLeadForm.name}
                  onChange={(e) => setCreateLeadForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-primary"
                  placeholder="Contact name"
                  disabled={creatingLead}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                <input
                  type="text"
                  value={createLeadForm.company}
                  onChange={(e) => setCreateLeadForm(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-primary"
                  placeholder="Company name"
                  disabled={creatingLead}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={createLeadForm.email}
                  onChange={(e) => setCreateLeadForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-primary"
                  placeholder="email@example.com"
                  disabled={creatingLead}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={createLeadForm.phone}
                  onChange={(e) => setCreateLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-primary"
                  placeholder="+1 234 567 8900"
                  disabled={creatingLead}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
              <input
                type="text"
                value={createLeadForm.designation}
                onChange={(e) => setCreateLeadForm(prev => ({ ...prev, designation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-primary"
                placeholder="CEO, Fund Manager, etc."
                disabled={creatingLead}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={createLeadForm.notes}
                onChange={(e) => setCreateLeadForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-primary resize-none h-20"
                placeholder="Additional notes..."
                disabled={creatingLead}
              />
            </div>
          </div>
        </Modal>

        {/* Link to Lead Modal (orphan meetings) */}
        <LinkLeadModal
          isOpen={showLinkLeadModal}
          onClose={() => setShowLinkLeadModal(false)}
          meeting={{ id: meetingId, title: meeting?.title }}
          onLinked={(lead) => navigate(`/leads/${lead.id}/meetings/${meetingId}`)}
        />
      </div>
    </DashboardLayout>
  );
};

export default MeetingDetailPage;

